/**
 * Extrae síntomas estructurados y respuestas Genie desde texto clínico narrativo
 * (historia del paciente, exploración física, conclusión).
 */

import type { GenieAnswers } from './genieEngine';

export interface InferredClinicalSymptom {
  present: boolean;
  severity?: string;
  location?: string;
  distribution?: string;
}

export type InferredSymptomsMap = Record<string, InferredClinicalSymptom>;

export interface ClinicalTextSources {
  notes?: string;
  diagnosis?: string;
  conclusion?: string;
}

/** Une fuentes clínicas en un solo bloque normalizado. */
export function buildClinicalText(sources: ClinicalTextSources): string {
  return [sources.notes, sources.diagnosis, sources.conclusion]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitClauses(text: string): string[] {
  return text
    .split(/(?<=[.;:])\s+|,\s+(?=[a-záéíóúñ])/i)
    .map(clause => clause.trim())
    .filter(clause => clause.length > 4);
}

function clauseHasNegatedTerm(clause: string, terms: RegExp): boolean {
  const lower = clause.toLowerCase();
  if (!terms.test(lower)) return false;
  return /\b(sin|no hay|niega|neg(?:a|ó)|ausencia de|no refiere|no presenta|no reporta)\b/i.test(lower);
}

function inferFromClauses(
  clauses: string[],
  terms: RegExp,
  negatedTerms?: RegExp
): boolean | undefined {
  let foundPositive = false;
  let foundNegative = false;

  for (const clause of clauses) {
    if (!terms.test(clause)) continue;
    if (negatedTerms && clauseHasNegatedTerm(clause, negatedTerms)) {
      foundNegative = true;
      continue;
    }
    if (clauseHasNegatedTerm(clause, terms)) {
      foundNegative = true;
      continue;
    }
    foundPositive = true;
  }

  if (foundPositive) return true;
  if (foundNegative) return false;
  return undefined;
}

function inferSeverity(clauses: string[], terms: RegExp): string {
  const match = clauses.find(c => terms.test(c));
  if (!match) return 'mild';
  if (/\b(sever[ao]|grave|marcad[ao]|importante|3\+|4\/5|profund[ao])\b/i.test(match)) return 'severe';
  if (/\b(moderad[ao]|medio|2\+|3\/5)\b/i.test(match)) return 'moderate';
  return 'mild';
}

function extractLowerLimbHint(text: string): string | undefined {
  if (/\b(miembros?\s+p[eé]lvicos?|mmii|piernas?|miembros?\s+inferiores?|lower limb)\b/i.test(text)) {
    return 'lower_limb';
  }
  if (/\b(miembros?\s+superiores?|mmss|brazos?|manos?|upper limb)\b/i.test(text)) {
    return 'upper_limb';
  }
  if (/\b(izquierd[ao]|left)\b/i.test(text)) return 'left';
  if (/\b(derech[ao]|right)\b/i.test(text)) return 'right';
  return undefined;
}

/** Infiere síntomas en formato ExtractedFileData.symptoms desde texto clínico. */
export function inferSymptomsFromClinicalText(rawText: string): InferredSymptomsMap | undefined {
  const clinicalText = rawText.toLowerCase().replace(/\s+/g, ' ').trim();
  if (!clinicalText) return undefined;

  const clauses = splitClauses(clinicalText);
  const symptoms: InferredSymptomsMap = {};

  const weaknessTerms = /\b(debilidad|weakness|paresia|par[aá]lisis|p[eé]rdida de fuerza|p[eé]rdida fuerza|flacidez)\b/i;
  const weaknessPresent = inferFromClauses(clauses, weaknessTerms, /\b(debilidad|weakness|paresia|par[aá]lisis|p[eé]rdida de fuerza)\b/i);
  if (weaknessPresent === true) {
    symptoms.weakness = {
      present: true,
      severity: inferSeverity(clauses, weaknessTerms),
      distribution: extractLowerLimbHint(clinicalText) || 'focal',
    };
  } else if (weaknessPresent === false) {
    symptoms.weakness = { present: false, severity: 'none' };
  }

  const sensoryTerms = /\b(parestesias?|paresthesias?|hormigueo|tingling|adormecimiento|numbness|hipoestesia|hipoestesias|entumecimiento|disestesias?)\b/i;
  const sensoryPresent = inferFromClauses(clauses, sensoryTerms, sensoryTerms);
  if (sensoryPresent === true) {
    symptoms.paresthesias = {
      present: true,
      severity: inferSeverity(clauses, sensoryTerms),
      distribution: extractLowerLimbHint(clinicalText) || 'focal',
    };
  } else if (sensoryPresent === false) {
    symptoms.paresthesias = { present: false, severity: 'none' };
  }

  const painTerms = /\b(dolor|pain|lumbalgia|ci[aá]tica|radicular)\b/i;
  const painPresent = inferFromClauses(clauses, painTerms, /\b(dolor|pain|lumbalgia|ci[aá]tica)\b/i);
  if (painPresent === true) {
    symptoms.pain = { present: true, severity: inferSeverity(clauses, painTerms) };
  } else if (painPresent === false) {
    symptoms.pain = { present: false, severity: 'none' };
  }

  const fascTerms = /\b(fasciculaciones?|fasciculations?|contracciones?|twitching)\b/i;
  const fascPresent = inferFromClauses(clauses, fascTerms, fascTerms);
  if (fascPresent === true) {
    symptoms.fasciculations = { present: true, severity: inferSeverity(clauses, fascTerms) };
  }

  const crampTerms = /\b(calambres?|cramps?|espasmos?|spasms?)\b/i;
  const crampPresent = inferFromClauses(clauses, crampTerms, crampTerms);
  if (crampPresent === true) {
    symptoms.cramps = { present: true, severity: inferSeverity(clauses, crampTerms) };
  }

  const gaitTerms = /\b(dificultad para caminar|claudicaci[oó]n|cojera|marcha alterada|walking difficulty)\b/i;
  const gaitPresent = inferFromClauses(clauses, gaitTerms, gaitTerms);
  if (gaitPresent === true) {
    symptoms.walkingDifficulty = { present: true, severity: inferSeverity(clauses, gaitTerms) };
  }

  const fatigueTerms = /\b(fatiga|fatigue|cansancio muscular)\b/i;
  const fatiguePresent = inferFromClauses(clauses, fatigueTerms, fatigueTerms);
  if (fatiguePresent === true) {
    symptoms.fatigue = { present: true, severity: inferSeverity(clauses, fatigueTerms) };
  }

  return Object.keys(symptoms).length > 0 ? symptoms : undefined;
}

export function inferSymptomsFromClinicalSources(sources: ClinicalTextSources): InferredSymptomsMap | undefined {
  return inferSymptomsFromClinicalText(buildClinicalText(sources));
}

/** Prefiere respuestas Genie (ventana síntomas) desde texto clínico. */
export function inferGenieAnswersFromClinicalText(rawText: string): GenieAnswers {
  const text = rawText.toLowerCase().replace(/\s+/g, ' ');
  if (!text) return {};

  const answers: GenieAnswers = {};

  // Curso temporal
  const monthsMatch = text.match(/\b(?:desde|inici(?:a|ó|ar)|noviembre|diciembre|enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre)\b[^.]{0,80}\b(20\d{2})\b/i);
  const weeksMatch = text.match(/\b(\d+)\s*(semanas?|weeks?)\b/i);
  if (/\b(agud[ao]|s[uú]bit[ao]|horas?|d[ií]as?)\b/i.test(text) && !/\bcron/i.test(text)) {
    answers.curso_temporal = 'agudo';
  } else if (weeksMatch) {
    const weeks = parseInt(weeksMatch[1], 10);
    if (weeks <= 4) answers.curso_temporal = 'agudo';
    else if (weeks <= 8) answers.curso_temporal = 'subagudo';
    else answers.curso_temporal = 'cronico';
  } else if (monthsMatch || /\b(meses?|a[nñ]os?|evoluci[oó]n|cr[oó]nic[ao])\b/i.test(text)) {
    answers.curso_temporal = 'cronico';
  } else if (/\b(subagud[ao]|4[\s-–]8\s*semanas)\b/i.test(text)) {
    answers.curso_temporal = 'subagudo';
  }

  // Distribución
  if (/\b(guante y calcet[ií]n|sim[eé]tric[ao].*distal|stocking.?glove)\b/i.test(text)) {
    answers.distribucion = 'simetrico_distal';
  } else if (/\b(proximal|cinturas?|gl[uú]teo|cu[aá]driceps|vasto)\b/i.test(text) &&
    /\b(debilidad|weakness|p[eé]rdida de fuerza)\b/i.test(text) &&
    !/\bdistal\b/i.test(text)) {
    answers.distribucion = 'simetrico_proximal';
  } else if (/\b(multifocal|mononeuritis|parchead[ao]|m[uú]ltiples nervios)\b/i.test(text)) {
    answers.distribucion = 'multifocal';
  } else if (/\b(asim[eé]tric[ao]|unilateral|focal|dermatoma|miotoma|izquierd[ao]|derech[ao]|mononeurop)\b/i.test(text)) {
    answers.distribucion = 'asimetrico_focal';
  } else if (/\b(generalizad[ao]|difus[ao]|bilateral)\b/i.test(text) &&
    /\b(hipoestesia|debilidad|parestesias?)\b/i.test(text)) {
    answers.distribucion = 'asimetrico_focal';
  }

  // Tipo de fibras
  const hasMotor = /\b(debilidad|weakness|paresia|p[eé]rdida de fuerza|atrofia|miotomas?\s+\d)\b/i.test(text);
  const hasSensory = /\b(hipoestesia|parestesias?|hormigueo|entumecimiento|dermatomas?|adormecimiento)\b/i.test(text);
  if (hasMotor && hasSensory) {
    answers.tipo_fibras = 'mixto';
  } else if (hasSensory) {
    answers.tipo_fibras = 'sensitivo_puro';
  } else if (hasMotor) {
    answers.tipo_fibras = 'motor_puro';
  }

  // Reflejos
  if (/\b(hiperreflexia|reflejos?\s*\+{2,}|reflejos?\s*(aumentados|vivos|exaltados)|babinski|clonus|clono)\b/i.test(text)) {
    answers.reflejos = 'hiperreflexia';
  } else if (/\b(reflejos?\s*(abolidos|ausentes|disminuidos|hipoactivos|deprimidos)|hiporreflexia|areflexia)\b/i.test(text)) {
    answers.reflejos = 'abolidos_disminuidos';
  } else if (/\b(reflejos?\s*(normales|conservados|presentes|\+\/\+))\b/i.test(text)) {
    answers.reflejos = 'normales';
  }

  // Fasciculaciones
  if (/\b(fasciculaciones?)\b/i.test(text) && !/\b(sin|no|niega)\s+fasciculaciones?\b/i.test(text)) {
    answers.fasciculaciones = 'si';
  }

  return answers;
}

export function inferGenieAnswersFromClinicalSources(sources: ClinicalTextSources): GenieAnswers {
  return inferGenieAnswersFromClinicalText(buildClinicalText(sources));
}
